import React, { useState, useEffect, useRef, useMemo } from "react";
import { POS_COLORS } from "../data/players";
import { buildPosTierData, loadOverrides, loadTierOrder, loadTierNames } from "../data/displayTiers";
import theme from "../data/theme";
import { StarToggle, AvoidToggle } from "./ListToggles";

const { colors, fonts } = theme;

const POSITIONS = ["QB", "RB", "WR", "TE"];
// Tab accent only — the row toggles' own colors live in ListToggles (same
// values: #eab308 lit, #334155 dim).
const STAR_COLOR = "#eab308";

/**
 * TierViewer — shows a scrollable tier list for the selected position.
 *
 * Props:
 *   players          — full player pool (UNIQUE_PLAYERS)
 *   currentPlayer    — player currently on the auction block (or null)
 *   draftPicks       — array of { player, price, ... } for sold players
 *   activeLeagueName — active league (selects the per-league display-tier edits)
 *   watchlist        — Set of starred player names
 *   onToggleWatchlist — fn(playerName) => toggle star
 *   avoids           — Set of avoided (do-not-draft) player names
 *   onToggleAvoid    — fn(playerName) => toggle avoid
 *
 * Tiers come from the SHARED display-tier composition (DISPLAY_TIER_MAPS + the
 * active league's userDisplayTier* edits) — the exact same source the Tier
 * Builder renders — NOT the raw TIER_MAPS pricing bands. TIER_MAPS still feeds
 * pricing/AI untouched; this is display-only.
 */
const TierViewer = React.memo(function TierViewer({
  players, currentPlayer, draftPicks, activeLeagueName,
  watchlist = new Set(), onToggleWatchlist,
  avoids = new Set(), onToggleAvoid,
  dynamicPrices,
}) {
  const autoPos = currentPlayer?.pos;
  const validAuto = POSITIONS.includes(autoPos) ? autoPos : null;
  const [selectedPos, setSelectedPos] = useState(validAuto || "RB");
  const [autoSnap, setAutoSnap] = useState(true);
  const [userOverride, setUserOverride] = useState(false);
  const scrollRef = useRef(null);
  const currentRef = useRef(null);
  const topAvailTierRef = useRef(null);

  const isWatchTab = selectedPos === "★";

  // Clear user override on each new nomination so auto-switch resumes
  useEffect(() => {
    if (currentPlayer) setUserOverride(false);
  }, [currentPlayer]);

  // Auto-select position when current player changes (unless user manually picked a tab)
  useEffect(() => {
    if (!userOverride && validAuto && validAuto !== selectedPos) {
      setSelectedPos(validAuto);
    }
  }, [userOverride, validAuto, selectedPos]);

  // Auto-scroll to current player's tier
  useEffect(() => {
    if (autoSnap && !isWatchTab && currentRef.current && scrollRef.current) {
      const t = setTimeout(() => {
        const container = scrollRef.current;
        const el = currentRef.current;
        const elTop = el.offsetTop - container.offsetTop;
        container.scrollTo({ top: Math.max(0, elTop - container.clientHeight / 2 + el.offsetHeight / 2), behavior: "smooth" });
      }, 80);
      return () => clearTimeout(t);
    }
  }, [autoSnap, isWatchTab, selectedPos, currentPlayer]);

  // Build sold-player lookup: name → price
  const soldMap = useMemo(() => {
    const m = new Map();
    (draftPicks || []).forEach(pick => m.set(pick.player.name, pick.price));
    return m;
  }, [draftPicks]);

  // Per-league display-tier edits (moves / order / names). Read once per mount
  // (and if the active league changes) — the same source the Tier Builder uses.
  const moves     = useMemo(() => loadOverrides(activeLeagueName), [activeLeagueName]);
  const tierOrder = useMemo(() => loadTierOrder(activeLeagueName), [activeLeagueName]);
  const tierNames = useMemo(() => loadTierNames(activeLeagueName), [activeLeagueName]);

  // Compose the selected position's tiers from DISPLAY_TIER_MAPS + edits.
  const { groups, pMap, tiers } = useMemo(
    () => buildPosTierData(players, selectedPos, moves, tierOrder),
    [players, selectedPos, moves, tierOrder]
  );

  // Group players into display tiers, labelled with custom names (band fallback).
  const tierGroups = useMemo(() => {
    return tiers.map(t => {
      const tierPlayers = (groups[t.tier] || []).map(n => pMap[n]).filter(Boolean);
      const customName = tierNames[`${selectedPos}:${t.tier}`];
      return { tier: t.tier, label: customName || t.label, players: tierPlayers };
    });
  }, [tiers, groups, pMap, tierNames, selectedPos]);

  // Watchlist view: starred players grouped by (position, display tier).
  const watchGroups = useMemo(() => {
    if (!isWatchTab) return [];
    const out = [];
    POSITIONS.forEach(pos => {
      const { groups: g, pMap: pm, tiers: ts } = buildPosTierData(players, pos, moves, tierOrder);
      ts.forEach(t => {
        const starred = (g[t.tier] || []).map(n => pm[n]).filter(p => p && watchlist.has(p.name));
        if (starred.length > 0) {
          const customName = tierNames[`${pos}:${t.tier}`];
          out.push({ pos, tier: t.tier, label: customName || t.label, players: starred });
        }
      });
    });
    // Sort by tier number first, then position
    out.sort((a, b) => a.tier - b.tier || POSITIONS.indexOf(a.pos) - POSITIONS.indexOf(b.pos));
    return out;
  }, [isWatchTab, players, watchlist, moves, tierOrder, tierNames]);

  // Tier-header $ range: max–min across ALL members (sold members use their sale
  // price, unsold use their dynamic estimate) so the range persists as members
  // sell. Null only when no member has a price at all.
  const priceRange = (grpPlayers) => {
    const prices = grpPlayers
      .map(p => soldMap.has(p.name) ? soldMap.get(p.name) : dynamicPrices?.get(`${p.name}-${p.pos}`))
      .filter(v => v != null);
    if (prices.length === 0) return null;
    return { min: Math.min(...prices), max: Math.max(...prices) };
  };

  // Find the tier number containing the highest-ranked undrafted player
  const topAvailTier = useMemo(() => {
    for (const group of tierGroups) {
      if (group.players.some(p => !soldMap.has(p.name))) return group.tier;
    }
    return null;
  }, [tierGroups, soldMap]);

  // Auto-scroll to top available tier when a player is drafted (and no player is on the block)
  useEffect(() => {
    if (userOverride || isWatchTab || !autoSnap) return;
    if (currentPlayer) return; // existing auto-scroll handles this case
    if (!topAvailTierRef.current || !scrollRef.current) return;
    const t = setTimeout(() => {
      const container = scrollRef.current;
      const el = topAvailTierRef.current;
      if (!container || !el) return;
      const elTop = el.offsetTop - container.offsetTop;
      container.scrollTo({ top: Math.max(0, elTop - 8), behavior: "smooth" });
    }, 120);
    return () => clearTimeout(t);
  }, [topAvailTier, userOverride, isWatchTab, autoSnap, currentPlayer, soldMap]);

  const posColor = POS_COLORS[selectedPos] || POS_COLORS.RB;
  const isOnBlock = (p) => currentPlayer && p.name === currentPlayer.name;
  const starCount = watchlist.size;

  const handleManualScroll = () => {
    if (autoSnap) setAutoSnap(false);
  };

  const selectPos = (pos) => {
    setSelectedPos(pos);
    setAutoSnap(false);
    setUserOverride(true);
  };

  // Both toggles come from the shared ListToggles buttons, so the tier panel
  // gets the same 44x44 mobile touch target as the player lists.
  const StarIcon = ({ playerName }) => (
    <StarToggle name={playerName} watchlist={watchlist} onToggleWatchlist={onToggleWatchlist} size={12} />
  );

  const AvoidIcon = ({ playerName }) => (
    <AvoidToggle name={playerName} avoids={avoids} onToggleAvoid={onToggleAvoid} size={11} />
  );

  return (
    <div style={{
      background: colors.bg.surface, border: `1px solid ${colors.border.subtle}`,
      borderRadius: 12, overflow: "hidden", display: "flex", flexDirection: "column",
    }}>
      {/* Position tabs */}
      <div style={{
        display: "flex", gap: 0, borderBottom: `1px solid ${colors.border.subtle}`,
        padding: "0 4px",
      }}>
        {POSITIONS.map(pos => {
          const pc = POS_COLORS[pos];
          const active = selectedPos === pos;
          return (
            <button key={pos} onClick={() => selectPos(pos)} style={{
              flex: 1, padding: "7px 0", fontSize: 10, fontWeight: 700, letterSpacing: "0.5px",
              fontFamily: fonts.display, cursor: "pointer", border: "none",
              background: "transparent",
              color: active ? pc.bg : colors.text.muted,
              borderBottom: active ? `2px solid ${pc.bg}` : "2px solid transparent",
              transition: "all 0.15s ease",
            }}>{pos}</button>
          );
        })}

        {/* Watchlist tab */}
        <button onClick={() => { setSelectedPos("★"); setUserOverride(true); setAutoSnap(false); }} style={{
          padding: "7px 8px", fontSize: 10, fontWeight: 700, letterSpacing: "0.5px",
          fontFamily: fonts.display, cursor: "pointer", border: "none",
          background: "transparent",
          color: isWatchTab ? STAR_COLOR : colors.text.faint,
          borderBottom: isWatchTab ? `2px solid ${STAR_COLOR}` : "2px solid transparent",
          transition: "all 0.15s ease",
          display: "flex", alignItems: "center", gap: 3,
        }}>
          ★{starCount > 0 && (
            <span style={{
              fontSize: 8, fontWeight: 800, lineHeight: 1,
              background: isWatchTab ? STAR_COLOR : colors.text.faint,
              color: isWatchTab ? "#000" : colors.bg.deep,
              borderRadius: 8, padding: "1px 4px", minWidth: 14, textAlign: "center",
            }}>{starCount}</span>
          )}
        </button>

        {/* Auto-snap button */}
        <button onClick={() => { setAutoSnap(true); setUserOverride(false); if (validAuto) setSelectedPos(validAuto); }} style={{
          padding: "7px 8px", fontSize: 9, fontWeight: 700, letterSpacing: "0.5px",
          fontFamily: fonts.display, cursor: "pointer", border: "none",
          background: "transparent",
          color: autoSnap && !isWatchTab ? colors.brand.orange : colors.text.faint,
          borderBottom: autoSnap && !isWatchTab ? `2px solid ${colors.brand.orange}` : "2px solid transparent",
          transition: "all 0.15s ease",
        }}>AUTO</button>
      </div>

      {/* Watchlist view */}
      {isWatchTab ? (
        <div ref={scrollRef} style={{ maxHeight: 400, overflowY: "auto", padding: "4px 0" }}>
          {watchGroups.length === 0 ? (
            <div style={{
              padding: "24px 16px", textAlign: "center",
              fontSize: 12, color: colors.text.muted, lineHeight: 1.6,
            }}>
              <div style={{ fontSize: 20, marginBottom: 8 }}>☆</div>
              No starred players yet.<br />
              Tap ☆ on any player to add them.
            </div>
          ) : watchGroups.map(({ pos, tier, label, players: grpPlayers }) => {
            const pc = POS_COLORS[pos];
            const range = priceRange(grpPlayers);
            return (
              <div key={`${pos}-${tier}`} style={{ marginBottom: 2 }}>
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "4px 10px", background: colors.bg.deep,
                  borderBottom: `1px solid ${colors.border.subtle}`,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{
                      fontSize: 9, fontWeight: 800, color: pc.bg,
                      background: `${pc.bg}18`, padding: "1px 6px", borderRadius: 3,
                      letterSpacing: "0.5px", fontFamily: fonts.display,
                    }}>{pos}</span>
                    <span style={{
                      fontSize: 9, fontWeight: 800, color: pc.bg,
                      background: `${pc.bg}18`, padding: "1px 6px", borderRadius: 3,
                      letterSpacing: "0.5px", fontFamily: fonts.display,
                    }}>T{tier}</span>
                    <span style={{ fontSize: 10, fontWeight: 600, color: colors.text.secondary }}>
                      {label}
                    </span>
                  </div>
                  {range && (
                    <span style={{ fontSize: 9, color: colors.text.faint, fontWeight: 600 }}>
                      ${range.min}–${range.max}
                    </span>
                  )}
                </div>
                {grpPlayers.map(player => {
                  const sold = soldMap.has(player.name);
                  const onBlock = isOnBlock(player);
                  const price = sold ? soldMap.get(player.name) : (dynamicPrices?.get(`${player.name}-${player.pos}`) ?? 1);
                  return (
                    <div key={player.name} style={{
                      display: "flex", alignItems: "center", gap: 6,
                      padding: "3px 10px",
                      borderBottom: `1px solid ${colors.border.subtle}`,
                      background: onBlock ? `${colors.brand.orange}12` : "transparent",
                      borderLeft: onBlock ? `2px solid ${colors.brand.orange}` : "2px solid transparent",
                      animation: onBlock ? "bidPulse 1.5s ease-in-out infinite" : "none",
                    }}>
                      <StarIcon playerName={player.name} />
                      <AvoidIcon playerName={player.name} />
                      <span style={{ fontSize: 9, fontWeight: 700, color: colors.text.faint, minWidth: 24 }}>
                        {player.posRank}
                      </span>
                      <span style={{
                        flex: 1, fontSize: 11, fontWeight: onBlock ? 700 : 500,
                        color: onBlock ? colors.brand.orange : sold ? colors.text.faint : colors.text.primary,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        textDecoration: sold ? "line-through" : "none",
                        opacity: avoids.has(player.name) ? 0.55 : 1,
                      }}>{avoids.has(player.name) ? <span style={{ fontSize: 9, marginRight: 3 }}>{"🚫"}</span> : null}{player.name}{player.team ? <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>{player.team}</span> : null}</span>
                      <span style={{
                        fontSize: 10, fontWeight: 700, minWidth: 28, textAlign: "right",
                        color: sold ? colors.text.faint : onBlock ? colors.brand.orange : colors.brand.accent,
                      }}>
                        {sold ? <s>${price}</s> : price != null ? `$${price}` : ""}
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      ) : (
        /* Normal tier list */
        <div ref={scrollRef} onScroll={handleManualScroll} style={{
          maxHeight: 400, overflowY: "auto", padding: "4px 0",
        }}>
          {tierGroups.map(group => {
            if (group.players.length === 0) return null;
            const range = priceRange(group.players);
            return (
              <div key={group.tier} ref={group.tier === topAvailTier ? topAvailTierRef : undefined} style={{ marginBottom: 2 }}>
                {/* Tier header */}
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "4px 10px", background: colors.bg.deep,
                  borderBottom: `1px solid ${colors.border.subtle}`,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{
                      fontSize: 9, fontWeight: 800, color: posColor.bg,
                      background: `${posColor.bg}18`, padding: "1px 6px", borderRadius: 3,
                      letterSpacing: "0.5px", fontFamily: fonts.display,
                    }}>T{group.tier}</span>
                    <span style={{ fontSize: 10, fontWeight: 600, color: colors.text.secondary }}>
                      {group.label}
                    </span>
                  </div>
                  {range && (
                    <span style={{ fontSize: 9, color: colors.text.faint, fontWeight: 600 }}>
                      ${range.min}–${range.max}
                    </span>
                  )}
                </div>

                {/* Players in this tier */}
                {group.players.map(player => {
                  const sold = soldMap.has(player.name);
                  const onBlock = isOnBlock(player);
                  const price = sold ? soldMap.get(player.name) : (dynamicPrices?.get(`${player.name}-${player.pos}`) ?? 1);
                  return (
                    <div
                      key={player.name}
                      ref={onBlock ? currentRef : undefined}
                      style={{
                        display: "flex", alignItems: "center", gap: 6,
                        padding: "3px 10px",
                        borderBottom: `1px solid ${colors.border.subtle}`,
                        background: onBlock ? `${colors.brand.orange}12` : "transparent",
                        borderLeft: onBlock ? `2px solid ${colors.brand.orange}` : "2px solid transparent",
                        animation: onBlock ? "bidPulse 1.5s ease-in-out infinite" : "none",
                      }}
                    >
                      <StarIcon playerName={player.name} />
                      <AvoidIcon playerName={player.name} />
                      <span style={{
                        fontSize: 9, fontWeight: 700, color: colors.text.faint, minWidth: 24,
                      }}>{player.posRank}</span>
                      <span style={{
                        flex: 1, fontSize: 11, fontWeight: onBlock ? 700 : 500,
                        color: onBlock ? colors.brand.orange : sold ? colors.text.faint : colors.text.primary,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        textDecoration: sold ? "line-through" : "none",
                        opacity: avoids.has(player.name) ? 0.55 : 1,
                      }}>{avoids.has(player.name) ? <span style={{ fontSize: 9, marginRight: 3 }}>{"🚫"}</span> : null}{player.name}{player.team ? <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>{player.team}</span> : null}</span>
                      <span style={{
                        fontSize: 10, fontWeight: 700, minWidth: 28, textAlign: "right",
                        color: sold ? colors.text.faint : onBlock ? colors.brand.orange : colors.brand.accent,
                      }}>
                        {sold ? <s>${price}</s> : price != null ? `$${price}` : ""}
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});

export default TierViewer;
